import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import type { ResolveFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom } from 'rxjs';
import { DealerFeature } from '../+state';
import type { Dealer } from '../api/api-types';
import { injectPartnerWebUrl } from '../providers/partner-web-url.token';
import { isHCM, isHWS } from '../providers/platform.token';
import { injectVehicleMarketUrl } from '../providers/vehicle-market-url.token';

export interface MetaData {
    description: string;
    ogTitle?: string;
    ogDescription?: string;
    image?: string;
    imageAlt?: string;
    type?: string;
}

/**
 * Wenn mehr Daten als nur der Dealer zum erstellen der Meta Daten gebraucht werden
 */
export function createMetaResolver(
    metaData: (store: Store, isHWS: boolean) => Promise<MetaData> | MetaData,
): ResolveFn<MetaData> {
    return async () => {
        const meta = inject(Meta);
        const store = inject(Store);
        const document = inject(DOCUMENT);
        const hws = isHWS();
        const baseUrl = hws ? injectPartnerWebUrl() : injectVehicleMarketUrl();
        return createMetaData(await metaData(store, hws), meta, document, baseUrl);
    };
}

export function createMetaResolverWithDealer(
    metaData: (dealer: Dealer, store: Store) => Promise<MetaData> | MetaData,
): ResolveFn<MetaData> {
    return async () => {
        const meta = inject(Meta);
        const store = inject(Store);
        const document = inject(DOCUMENT);
        if (isHCM()) {
            console.error("createMetaResolverWithDealer can't  be used in vehicle market");
        }
        const partnerWebsiteUrl = injectPartnerWebUrl();
        const dealer = await firstValueFrom(
            store.select(DealerFeature.state.selectLoadedDealer).pipe(filter((d) => Boolean(d))),
        );
        return createMetaData(await metaData(dealer, store), meta, document, partnerWebsiteUrl);
    };
}

export function createMetaData(data: MetaData, meta: Meta, document: Document, url: string | null): MetaData {
    updateMetaTag(meta, 'description', data.description);
    createTwitterMetaData(data, meta);
    createOgMetaData(data, meta, document, url);
    return data;
}

export function createOgMetaData(data: MetaData, meta: Meta, document: Document, baseUrl: string | null): void {
    updateMetaTag(meta, 'og:title', data.ogTitle);
    updateMetaTag(meta, 'og:description', data.ogDescription);
    updateMetaTag(meta, 'og:image', data.image);
    updateMetaTag(meta, 'og:image:alt', data.imageAlt);
    updateMetaTag(meta, 'og:type', data.type ?? 'website');
    updateMetaTag(meta, 'og:locale', 'de_DE');
    updateMetaTag(meta, 'og:site_name', 'Hyundai Motor Deutschland GmbH');
    if (baseUrl) {
        updateMetaTag(meta, 'og:url', getCurrentUrl(document, baseUrl));
    }
}

export function createTwitterMetaData(data: MetaData, meta: Meta): void {
    updateMetaTag(meta, 'twitter:card', 'summary_large_image');
    updateMetaTag(meta, 'twitter:title', data.ogTitle);
    updateMetaTag(meta, 'twitter:description', data.ogDescription);
    updateMetaTag(meta, 'twitter:image', data.image);
    updateMetaTag(meta, 'twitter:image:alt', data.imageAlt);
}

export type MetaAttrType = 'property' | 'name';

function getMetaAttrType(tagName: string): MetaAttrType {
    return tagName.startsWith('og:') ? 'property' : 'name';
}

function buildMetaSelector(tagName: string, attrType: MetaAttrType): string {
    return `${attrType}="${tagName}"`;
}

function updateMetaTag(meta: Meta, tagName: string, content?: string): void {
    const attrType = getMetaAttrType(tagName);
    const selector = buildMetaSelector(tagName, attrType);

    if (!content) {
        meta.removeTag(selector);
        return;
    }

    const tagDefinition = { [attrType]: tagName, content };

    if (meta.getTag(selector)) {
        meta.updateTag(tagDefinition);
    } else {
        meta.addTag(tagDefinition);
    }
}

export function getCurrentUrl(document: Document, baseUrl: string): string {
    return `${baseUrl}${document.location.pathname}`;
}

export function getDefaultOgImage(dealer: Dealer, baseUrl?: string): { image: string; alt: string } {
    let ogImage = dealer.menuImage?.url;
    const ogImageAlt = dealer.menuImage?.alt;
    const ogImageFallback =
        'https://res.cloudinary.com/hmd-pw-dfnosvs4u/image/upload/w_1600,h_900,c_fill,q_80,f_webp/_defaults/about-us.heroImage';
    const ogImageAltFallback =
        'Ein modernes Bürogebäude mit einer Glasfassade und dem Hyundai Logo steht an einer begrünten Straße. Vor dem Gebäude wachsen mehrere Bäume, die einen Teil der Fassade teilweise verdecken.';
    //If ogImage string begins with _defaults,
    if (ogImage && (ogImage.startsWith('_defaults') || ogImage.startsWith(dealer.id))) {
        ogImage = `${baseUrl}/image/upload/w_1600,h_900,c_fill,q_80,f_webp/${ogImage}`;
    }
    return {
        image: ogImage ?? ogImageFallback,
        alt: ogImageAlt ?? ogImageAltFallback,
    };
}
